import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ContractService } from 'src/app/contract/shared/contract.service';
import { ToastrService } from 'ngx-toastr';
import * as moment from 'moment';

@Component({
  selector: 'app-reject-reason-dialog',
  templateUrl: './reject-reason-dialog.component.html',
  styleUrl: './reject-reason-dialog.component.css'
})
export class RejectReasonDialogComponent implements OnInit {
  rejectionReasons: any[] = [];
  loading: boolean = true;
  currentReason: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private contractService: ContractService,
    private toast: ToastrService
  ) {
    this.currentReason = data.reason || data.description || '';
  }

  async ngOnInit() {
    // If contractId and userId are provided, fetch all rejection reasons from audit logs
    if (this.data.contractId && (this.data.userId || this.data.reviewerId || this.data.approverId)) {
      await this.fetchAllRejectionReasons();
    } else {
      // Fallback to showing just the current reason
      if (this.currentReason) {
        const extractedReason = this.extractReasonText(this.currentReason);
        if (extractedReason) {
          this.rejectionReasons = [{
            reason: extractedReason,
            date: null,
            action: 'Declined'
          }];
        }
      }
      this.loading = false;
    }
  }

  async fetchAllRejectionReasons() {
    this.loading = true;
    try {
      const auditLogsResult = await this.contractService.getAuditLogs(this.data.contractId);
      const auditLogs: any[] = Array.isArray(auditLogsResult) ? auditLogsResult : [];
      
      // Filter for rejection/decline actions
      const rejectionActions = auditLogs.filter(log => {
        const action = (log.action || '').toLowerCase();
        return action.includes('decline') || action.includes('reject') || action.includes('rejected');
      });

      // Extract rejection reasons with their dates
      // Use a Map to track the most recent occurrence of each unique reason
      const reasonsMap = new Map<string, any>();
      
      // Process audit logs to extract rejection reasons
      rejectionActions.forEach(log => {
        // Try to extract reason from action text or description
        let reason = '';
        if (log.description) {
          reason = this.extractReasonText(log.description);
        } else if (log.action) {
          reason = this.extractReasonText(log.action);
        }

        if (reason) {
          const logDate = log.createdDate ? new Date(log.createdDate) : null;
          const existingEntry = reasonsMap.get(reason);
          
          // If this reason doesn't exist, or if this log entry is more recent, update it
          if (!existingEntry || (logDate && (!existingEntry.originalDate || logDate > existingEntry.originalDate))) {
            reasonsMap.set(reason, {
              reason: reason,
              date: logDate ? moment(logDate).format('MMM D, YYYY h:mm A') : null,
              originalDate: logDate, // Store original date for sorting
              action: log.action || 'Declined'
            });
          }
        }
      });

      // Convert to array and sort by date (most recent first)
      this.rejectionReasons = Array.from(reasonsMap.values()).sort((a, b) => {
        // Items with dates come first, sorted by most recent
        if (a.originalDate && b.originalDate) {
          return b.originalDate.getTime() - a.originalDate.getTime();
        }
        // Items with dates come before items without dates
        if (a.originalDate && !b.originalDate) return -1;
        if (!a.originalDate && b.originalDate) return 1;
        // Both without dates - maintain order
        return 0;
      });
      
      // If no reasons found in audit logs but we have current reason, use that
      if (this.rejectionReasons.length === 0 && this.currentReason) {
        const extractedReason = this.extractReasonText(this.currentReason);
        if (extractedReason) {
          this.rejectionReasons = [{
            reason: extractedReason,
            date: null,
            action: 'Declined'
          }];
        }
      }
    } catch (error) {
      console.error('Error fetching rejection reasons:', error);
      // Fallback to current reason
      if (this.currentReason) {
        const extractedReason = this.extractReasonText(this.currentReason);
        if (extractedReason) {
          this.rejectionReasons = [{
            reason: extractedReason,
            date: null,
            action: 'Declined'
          }];
        }
      }
    } finally {
      this.loading = false;
    }
  }

  /**
   * Extracts only the actual rejection reason text, removing prefixes like
   * "Contract Review is declined by [Name] Reason for Decline: "
   */
  private extractReasonText(text: string): string {
    if (!text) return '';

    // Pattern to match "Reason for Decline: " followed by the actual reason (may be in quotes)
    // Examples:
    // "Contract Review is declined by Dheeraj Gupta Reason for Decline: "some reason""
    // "Contract Review is declined by Dheeraj Gupta Reason for Decline: some reason"
    const reasonPattern = /Reason\s+for\s+Decline:\s*["']?([^"']+)["']?/i;
    const match = text.match(reasonPattern);
    
    if (match && match[1]) {
      return match[1].trim();
    }

    // Fallback: Try to find text after "declined" or "rejected" followed by colon
    const declinedPattern = /(?:declined|rejected)[:\s]+(?:by\s+[^:]+:\s*)?["']?([^"']+)["']?/i;
    const declinedMatch = text.match(declinedPattern);
    
    if (declinedMatch && declinedMatch[1]) {
      const extracted = declinedMatch[1].trim();
      // Remove "Reason for Decline: " if it's still in the extracted text
      const reasonMatch = extracted.match(/Reason\s+for\s+Decline:\s*["']?([^"']+)["']?/i);
      if (reasonMatch && reasonMatch[1]) {
        return reasonMatch[1].trim();
      }
      return extracted;
    }

    // If no pattern matches, return the original text
    return text.trim();
  }
}
